import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { isUuid } from 'components/marketplace/helpers/useSearchQueries';
import { getAnonymousId } from 'components/shared/trackers';

import { MarketplaceRegionPaginationArmDocument } from './__generated__/region-pagination.zeus';

const EXPERIMENT_KEY = 'resale-26-08-region-page-pagination';

const ARMS = ['control', 'treatment'] as const;

export type RegionPaginationArm = (typeof ARMS)[number];

const isArm = (value: string): value is RegionPaginationArm =>
  (ARMS as readonly string[]).includes(value);

/**
 * QA override. The older `opendoor.cosmos.<flag>` mechanism only understands
 * `'true'`, so it cannot name control; this reads the arm itself, which keeps
 * control reachable without a GrowthBook force rule.
 */
const overriddenArm = (): RegionPaginationArm | null => {
  try {
    const stored = window.localStorage.getItem(`opendoor.cosmos.${EXPERIMENT_KEY}`);
    return stored != null && isArm(stored) ? stored : null;
  } catch {
    return null;
  }
};

interface AssignmentIdentity {
  customerId: string | null | undefined;
  anonymousId: string | null | undefined;
}

const usableId = (value: string | null | undefined): string | undefined =>
  value != null && isUuid(value) ? value : undefined;

const resolveArm = async ({
  customerId,
  anonymousId,
}: AssignmentIdentity): Promise<RegionPaginationArm | null> => {
  const customer = {
    ...(usableId(customerId) != null ? { customerId } : {}),
    ...(usableId(anonymousId) != null ? { anonymousId } : {}),
  };

  const result = await requestZeusFromBrowser(MarketplaceRegionPaginationArmDocument, {
    // The server records the impression alongside the assignment, so the
    // variation index travels with it rather than being re-derived here — the
    // arm string alone is dropped by GrowthBook's assignment queries.
    input: { autoImpression: true, grain: { customer }, key: EXPERIMENT_KEY },
  });

  const evaluated = result.evaluateExperimentVariant;
  if (evaluated.__typename !== 'EvaluateExperimentVariantPayload') {
    return null;
  }
  const value = evaluated.assignment.value;
  return value != null && isArm(value) ? value : null;
};

interface RegionPaginationArmOptions {
  /** Customer UUID once auth settles; undefined while it is still in flight. */
  customerId: string | null | undefined;
  /**
   * False while auth is in flight. `user` is undefined until it settles, so
   * assigning before then buckets a logged-in visitor on their anonymous id and
   * again on their customer id — two impressions on two different hashes, which
   * flip the arm under them for no reason they can see.
   */
  isAuthResolved: boolean;
  /**
   * False where every arm renders the same control, so an impression is only
   * recorded where the arms actually differ. Zone routes and single-page markets
   * show Show more either way.
   */
  isEligible: boolean;
}

/**
 * Resolves the visitor's arm client-side. It cannot be resolved during SSR: the
 * route is cache-safe and Cloudflare keys on the URL, so the render a crawler
 * receives is shared by everyone. Callers therefore treat anything other than a
 * settled `control` as the paginated default.
 */
export const useRegionPaginationArm = ({
  customerId,
  isAuthResolved,
  isEligible,
}: RegionPaginationArmOptions): RegionPaginationArm | null => {
  // Both read the browser, so they settle after mount rather than during a
  // render that also runs on the server.
  const [override, setOverride] = useState<RegionPaginationArm | null>(null);
  const [anonymousId, setAnonymousId] = useState<string>();
  // Gates the assignment as well: enabling before this settles would assign a
  // logged-in visitor on a grain missing their anonymous id, since a valid
  // customer id alone is enough to satisfy `hasIdentity`.
  const [hasReadBrowser, setHasReadBrowser] = useState(false);
  useEffect(() => {
    setOverride(overriddenArm());
    setAnonymousId(getAnonymousId());
    setHasReadBrowser(true);
  }, []);

  // Keyed on both identifiers so a late-arriving anonymous id assigns rather
  // than leaving the visitor permanently unenrolled.
  const identity: AssignmentIdentity = { customerId, anonymousId };
  const hasIdentity = usableId(customerId) != null || usableId(anonymousId) != null;

  const { data } = useQuery({
    queryKey: [
      'marketplace',
      'region-pagination-arm',
      customerId ?? null,
      anonymousId ?? null,
    ] as const,
    queryFn: () => resolveArm(identity),
    enabled: hasReadBrowser && override == null && isAuthResolved && isEligible && hasIdentity,
    // One assignment per visitor per identity: re-running it would record a
    // second impression for the same exposure.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  return override ?? data ?? null;
};
